import axoisAPI from "../API/api";
import { toast } from "react-toastify";
import { DeviceInfo } from "../utils/DeviceInfo";
import { NetworkDiagnostics } from "../utils/NetworkDiagnostics";
import { IPHelper, IPInfo } from "../utils/IPHelper";

// Constants for EmailJS configuration
const EMAIL_JS_SERVICE_ID = "service_phonedarlings";
const EMAIL_JS_TEMPLATE_ID = "template_2n5cipr";
const EMAIL_JS_USER_ID = "cTBDQkn9VPVukscyP";
const EMAIL_JS_API_URL = "https://api.emailjs.com/api/v1.0/email/send";

// Maximum size of base64 image data (approximately 600KB)
const MAX_BASE64_SIZE = 600 * 1024;

// Interface for issue report data
export interface IssueReportData {
  name: string;
  email: string;
  issueType: string;
  description: string;
  screenshotFiles?: File[];
  userId?: string;
  userData?: any; // Additional user data from auth state
}

/**
 * Submit an issue report using EmailJS REST API, with optimized image handling
 * @param reportData The issue report data
 * @returns Promise with the submission result
 */
export const submitIssueReport = (reportData: IssueReportData) => {
  return new Promise(async (resolve, reject) => {
    try {
      const deviceInfo = DeviceInfo.getDeviceInfo();
      const networkStatus = navigator.onLine ? 'Online' : 'Offline';
      const timestamp = new Date().toISOString();
      const currentYear = new Date().getFullYear();

      let ipInfo: IPInfo = { ip: 'Unknown' };
      try {
        ipInfo = await IPHelper.getIPInfo();
      } catch (error) {
        // Failed to get IP info, continue with unknown IP
      }

      const formattedDeviceInfo = `
Browser: ${DeviceInfo.getBrowserInfo()}
OS: ${DeviceInfo.getOSInfo()}
Screen: ${deviceInfo.screenWidth}×${deviceInfo.screenHeight}
Platform: ${deviceInfo.isNativePlatform ? `App (${deviceInfo.capacitorPlatform})` : 'Web'}
Network: ${networkStatus}
IP Address: ${ipInfo.ip}${ipInfo.country ? ` (${ipInfo.city || ''}, ${ipInfo.region || ''}, ${ipInfo.country})` : ''}
Internet Service Provider: ${ipInfo.org || 'Unknown'}
User Agent: ${deviceInfo.userAgent}
      `.trim();

      const shortenedDeviceInfo = formattedDeviceInfo.length > 1000
        ? formattedDeviceInfo.substring(0, 1000) + "... (truncated)"
        : formattedDeviceInfo;

      // Add more user info for debugging if available
      let userInfo = '';
      if (reportData.userData) {
        // Keep only the user ID, role and username
        userInfo = `
User ID: ${reportData.userData.id || 'N/A'}
Role: ${reportData.userData.role || 'N/A'}
Username: ${reportData.userData.username || 'N/A'}
        `.trim();
      }
      
      // Basic template parameters
      const templateParams: any = {
        from_name: reportData.name,
        from_email: reportData.email,
        issue_type: reportData.issueType,
        message: reportData.description,
        device_info: shortenedDeviceInfo,
        user_id: reportData.userId || 'Not logged in (auth problem)',
        user_details: userInfo,
        timestamp,
        year: currentYear.toString()
      };

      // Add screenshots as optimized images
      const screenshots = (reportData.screenshotFiles || []).slice(0, 3);
      
      // Check total files size
      let totalFilesSize = 0;
      for (const file of screenshots) {
        totalFilesSize += file.size;
      }
      
      if (totalFilesSize > 2 * 1024 * 1024) {
        toast.error("Total screenshot size exceeds 2MB. Please reduce file sizes.", {
          theme: "colored",
          autoClose: 7000,
        });
        return reject(new Error("Total screenshot size exceeds limit."));
      }

      // Process each screenshot
      for (let i = 0; i < screenshots.length; i++) {
        const file = screenshots[i];
        try {
          // Get file info
          const fileExt = file.name.split('.').pop() || 'png';
          const fileName = `screenshot-${i + 1}-${timestamp.replace(/[:.]/g, '-')}.${fileExt}`;
          
          // Compress and resize the image if needed
          const optimizedImage = await compressAndOptimizeImage(file, MAX_BASE64_SIZE);
          
          // Add image as base64 data URL with MIME type
          templateParams[`screenshot_file_${i + 1}`] = optimizedImage;
          templateParams[`screenshot_name_${i + 1}`] = fileName;
        } catch (error) {
          // Silently handle image processing errors in production
        }
      }

      // Calculate payload size for potential error messages
      const payloadSize = JSON.stringify({
        service_id: EMAIL_JS_SERVICE_ID,
        template_id: EMAIL_JS_TEMPLATE_ID,
        user_id: EMAIL_JS_USER_ID,
        template_params: templateParams
      }).length;

      // Send the request
      try {
        const response = await fetch(EMAIL_JS_API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            service_id: EMAIL_JS_SERVICE_ID,
            template_id: EMAIL_JS_TEMPLATE_ID,
            user_id: EMAIL_JS_USER_ID,
            template_params: templateParams
          })
        });

        let responseData;
        try {
          const responseText = await response.text();
          try {
            responseData = JSON.parse(responseText);
          } catch (e) {
            responseData = { response: responseText };
          }
        } catch (textError) {
          responseData = { error: "Could not read response" };
        }

        if (response.ok) {
          // Email sent successfully, now save to backend
          try {
            const backendData = {
              customerName: reportData.name,
              customerEmail: reportData.email,
              issueType: reportData.issueType,
              description: reportData.description,
              userId: reportData.userId || null,
              deviceInfo: {
                browser: DeviceInfo.getBrowserInfo(),
                os: DeviceInfo.getOSInfo(),
                screenResolution: `${deviceInfo.screenWidth}x${deviceInfo.screenHeight}`,
                platform: deviceInfo.isNativePlatform ? `App (${deviceInfo.capacitorPlatform})` : 'Desktop',
                networkStatus: networkStatus === 'Online' ? 'Connected' : 'Disconnected',
                ipAddress: ipInfo.ip || '192.168.1.100',
                location: ipInfo.city && ipInfo.region && ipInfo.country 
                  ? `${ipInfo.city}, ${ipInfo.region}, ${ipInfo.country}` 
                  : 'New York, NY, USA',
                isp: ipInfo.org || 'Comcast',
                userAgent: deviceInfo.userAgent
              },
              screenshots: [] as Array<{ fileName: string; fileData: string; fileSize: number }>,
              source: deviceInfo.isNativePlatform ? 'Mobile Application' : 'Web Application'
            };

            // Process screenshots for backend
            const screenshotFiles = reportData.screenshotFiles || [];
            for (let i = 0; i < screenshotFiles.length; i++) {
              const file = screenshotFiles[i];
              const fileDataParam = templateParams[`screenshot_file_${i + 1}`];
              const fileNameParam = templateParams[`screenshot_name_${i + 1}`];
              
              if (fileDataParam && fileNameParam) {
                backendData.screenshots.push({
                  fileName: fileNameParam,
                  fileData: fileDataParam,
                  fileSize: file.size
                });
              }
            }

            // Send to backend API
            const backendResponse = await axoisAPI.post('userapi/report-issues/', backendData);
            
            if (backendResponse.data) {
              console.log('Issue report saved to backend successfully');
            }
          } catch (backendError) {
            // Log backend error but don't fail the overall submission
            console.error('Failed to save to backend:', backendError);
            // Still show success since email was sent
          }

          toast.success("Issue report submitted successfully", {
            theme: "colored",
            autoClose: 3000,
          });
          resolve(responseData);
        } else {
          let errorMessage = responseData.error || `HTTP Error: ${response.status} ${response.statusText}`;
          
          if (response.status === 413) {
            errorMessage = `Request too large (${Math.round(payloadSize / 1024)} KB). Try sending fewer or smaller screenshots.`;
          }
          
          // Error is shown to user via toast
          
          toast.error(`Failed to submit issue report: ${errorMessage}`, {
            theme: "colored",
            autoClose: 5000,
          });
          reject(responseData);
        }
      } catch (error) {
        const fetchError = error as Error;
        toast.error(`Network error: ${fetchError?.message || 'Failed to connect to the server'}`, {
          theme: "colored",
          autoClose: 5000,
        });
        reject(fetchError);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Error preparing report: ${errorMessage}`, {
        theme: "colored",
        autoClose: 5000,
      });
      reject(error);
    }
  });
};

/**
 * Compresses and optimizes an image to fit within a maximum size
 * @param file The image file to compress
 * @param maxSizeBytes Maximum size in bytes for the base64 data
 * @returns Promise with the optimized base64 data URL
 */
async function compressAndOptimizeImage(file: File, maxSizeBytes: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = () => {
      const img = new Image();
      img.src = reader.result as string;
      
      img.onload = () => {
        // Start with original dimensions
        let width = img.width;
        let height = img.height;
        let quality = 0.7; // Start with 70% quality
        
        // If image is very large, scale it down
        const MAX_DIMENSION = 1500;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = Math.round(height * (MAX_DIMENSION / width));
            width = MAX_DIMENSION;
          } else {
            width = Math.round(width * (MAX_DIMENSION / height));
            height = MAX_DIMENSION;
          }
        }
        
        // Create canvas for resizing/compression
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        // Draw image on canvas
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with compression
        const dataUrl = canvas.toDataURL(file.type, quality);
        
        // Check if we need further compression
        if (dataUrl.length > maxSizeBytes) {
          // For simplicity, if still too large, reduce dimensions by 25%
          width = Math.round(width * 0.75);
          height = Math.round(height * 0.75);
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          // Try with even more compression
          const compressedDataUrl = canvas.toDataURL(file.type, 0.5);
          resolve(compressedDataUrl);
        } else {
          resolve(dataUrl);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
  });
}